import { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { ToastMessage, ToastType, ToastContainer } from '../components/ui/Toast';

interface ToastContextValue {
  showToast: (options: {
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  }) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, position = 'bottom-right' }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((options: {
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  }) => {
    const id = Date.now().toString();
    const toast: ToastMessage = {
      id,
      type: options.type,
      title: options.title,
      message: options.message,
      duration: options.duration ?? 5000,
      action: options.action,
    };

    setToasts((prev) => [...prev, toast]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} position={position} />
    </ToastContext.Provider>
  );
};

// Convenience methods
export const toast = {
  success: (title: string, options?: { message?: string; duration?: number; action?: { label: string; onClick: () => void } }) => {
    // This will be populated by the provider
    console.warn('Toast called outside of ToastProvider context');
  },
  error: (title: string, options?: { message?: string; duration?: number; action?: { label: string; onClick: () => void } }) => {
    console.warn('Toast called outside of ToastProvider context');
  },
  warning: (title: string, options?: { message?: string; duration?: number; action?: { label: string; onClick: () => void } }) => {
    console.warn('Toast called outside of ToastProvider context');
  },
  info: (title: string, options?: { message?: string; duration?: number; action?: { label: string; onClick: () => void } }) => {
    console.warn('Toast called outside of ToastProvider context');
  },
};